import { useEffect, useState, useCallback } from "react";
import {
  useHMSActions,
  useHMSStore,
  selectPeers,
  selectIsConnectedToRoom,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  useVideo,
} from "@100mslive/react-sdk";
import {
  Microphone,
  MicrophoneSlash,
  VideoCamera,
  VideoCameraSlash,
  PhoneDisconnect,
  User,
} from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./VideoMeetingRoom.css";

/**
 * Shared 100ms call UI - used by both the therapist page
 * (/dashboard/video-meeting/:appointmentId) and the patient's public page
 * (/meet/:linkId). Renders prejoin (join button + device toggles), the call
 * grid, and the control bar. Must be mounted inside <HMSRoomProvider>.
 *
 * Props:
 * - authToken: 100ms auth token (host or guest)
 * - userName: display name for the local peer
 * - title / subtitle: header texts
 * - onLeft: callback after leaving the room
 */

function PeerTile({ peer }) {
  const { videoRef } = useVideo({ trackId: peer.videoTrack });
  return (
    <div className={`vmr-tile ${peer.isLocal ? "local" : ""}`}>
      <video
        ref={videoRef}
        className="vmr-tile-video"
        autoPlay
        muted={peer.isLocal}
        playsInline
      />
      {!peer.videoTrack && (
        <div className="vmr-tile-avatar">
          <User size={42} weight="fill" />
        </div>
      )}
      <span className="vmr-tile-name">
        {peer.name}
        {peer.isLocal ? " (אני)" : ""}
      </span>
    </div>
  );
}

function VideoMeetingRoom({ authToken, userName, title, subtitle, onLeft }) {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const peers = useHMSStore(selectPeers);
  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);

  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const handleJoin = useCallback(async () => {
    setIsJoining(true);
    setJoinError(null);
    try {
      await hmsActions.join({
        userName: userName || "משתתף",
        authToken,
      });
    } catch (error) {
      console.error("[CK-6061] Error joining video room:", {
        errorCode: "CK-6061",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
      setJoinError(
        "לא ניתן להצטרף לשיחה. ודאו שאישרתם גישה למצלמה ולמיקרופון ונסו שוב."
      );
    } finally {
      setIsJoining(false);
    }
  }, [hmsActions, authToken, userName]);

  const handleLeave = useCallback(async () => {
    try {
      await hmsActions.leave();
    } catch (e) {
      // leaving best-effort
    }
    if (onLeft) onLeft();
  }, [hmsActions, onLeft]);

  // Leave the room when the component unmounts / tab closes
  useEffect(() => {
    const onUnload = () => hmsActions.leave();
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      hmsActions.leave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isConnected) {
    return (
      <div className="vmr-prejoin">
        <div className="vmr-prejoin-icon">
          <VideoCamera size={30} weight="fill" />
        </div>
        <h2 className="vmr-prejoin-title">{title}</h2>
        {subtitle && <p className="vmr-prejoin-sub">{subtitle}</p>}
        {joinError && <div className="vmr-error">{joinError}</div>}
        <button
          type="button"
          className="vmr-join-btn"
          onClick={handleJoin}
          disabled={isJoining}
        >
          {isJoining ? (
            <>
              <LoaderLoop size="xs" /> מצטרף...
            </>
          ) : (
            <>
              <VideoCamera size={16} weight="fill" /> הצטרפות לשיחה
            </>
          )}
        </button>
        <p className="vmr-prejoin-hint">
          בלחיצה על הצטרפות, הדפדפן יבקש גישה למצלמה ולמיקרופון
        </p>
      </div>
    );
  }

  return (
    <div className="vmr-call">
      <div
        className={`vmr-grid ${peers.length <= 1 ? "solo" : peers.length === 2 ? "duo" : "many"}`}
      >
        {peers.map((peer) => (
          <PeerTile key={peer.id} peer={peer} />
        ))}
      </div>
      {peers.length <= 1 && (
        <div className="vmr-waiting">ממתינים למשתתף הנוסף...</div>
      )}
      <div className="vmr-controls">
        <button
          type="button"
          className={`vmr-ctrl-btn ${isAudioOn ? "" : "off"}`}
          onClick={() => hmsActions.setLocalAudioEnabled(!isAudioOn)}
          aria-label={isAudioOn ? "השתקת מיקרופון" : "הפעלת מיקרופון"}
        >
          {isAudioOn ? (
            <Microphone size={20} weight="fill" />
          ) : (
            <MicrophoneSlash size={20} weight="fill" />
          )}
        </button>
        <button
          type="button"
          className={`vmr-ctrl-btn ${isVideoOn ? "" : "off"}`}
          onClick={() => hmsActions.setLocalVideoEnabled(!isVideoOn)}
          aria-label={isVideoOn ? "כיבוי מצלמה" : "הפעלת מצלמה"}
        >
          {isVideoOn ? (
            <VideoCamera size={20} weight="fill" />
          ) : (
            <VideoCameraSlash size={20} weight="fill" />
          )}
        </button>
        <button
          type="button"
          className="vmr-ctrl-btn leave"
          onClick={handleLeave}
          aria-label="יציאה מהשיחה"
        >
          <PhoneDisconnect size={20} weight="fill" />
        </button>
      </div>
    </div>
  );
}

export default VideoMeetingRoom;
